import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, Loader2, Mail, MessageCircle, Copy, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CURRENCIES = ["usd", "eur", "gbp", "inr", "aed", "sgd", "aud", "cad"];
const STATUS = {
  pending: { t: "Pending", c: "border-amber-200 bg-amber-50 text-amber-700", Icon: Clock },
  paid: { t: "Paid", c: "border-green-200 bg-green-50 text-green-700", Icon: CheckCircle2 },
  expired: { t: "Expired", c: "border-red-200 bg-red-50 text-red-600", Icon: XCircle },
};
const fmtAmt = (l) => `${(l.currency || "usd").toUpperCase()} ${Number(l.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function PaymentLinkManager({ req }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(null);
  const [services, setServices] = useState([]);
  const [email, setEmail] = useState(req.client_email || "");
  const [service, setService] = useState(req.package_name || "");
  const [amount, setAmount] = useState(req.amount || "");
  const [currency, setCurrency] = useState((req.currency || "usd").toLowerCase());
  const [desc, setDesc] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/payment-links", { params: { request_id: req.id } }); setLinks(r.data.links || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [req.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/catalog").then((r) => {
      const names = [...(r.data.countries || []).map((c) => c.name), "Company Formation", "Bank Account Opening", "Annual Compliance", "Consultation"];
      setServices([...new Set(names.filter(Boolean))]);
    }).catch(() => {});
  }, []);

  const generate = async () => {
    if (!service.trim()) return toast.error("Enter a service name.");
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount.");
    if (!email.includes("@")) return toast.error("Enter the client's email.");
    setCreating(true);
    try {
      await api.post("/admin/payment-links", { request_id: req.id, client_email: email, service_name: service, amount: Number(amount), currency, description: desc });
      toast.success("Payment link generated.");
      setDesc("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create link."); } finally { setCreating(false); }
  };

  const copy = (u) => { navigator.clipboard?.writeText(u); toast.success("Link copied."); };
  const sendEmail = async (sid) => {
    setSending(sid);
    try { await api.post(`/admin/payment-links/${sid}/send-email`); toast.success("Payment email sent."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email."); } finally { setSending(null); }
  };
  const whatsapp = (l) => {
    const text = encodeURIComponent(`Hi ${l.client_name || "there"}, here is your secure payment link for ${l.service_name} (${fmtAmt(l)}): ${l.url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  return (
    <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-4 space-y-4" data-testid={`pl-manager-${req.id}`}>
      <div className="rounded-md border border-border bg-card p-3 space-y-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5 text-orange-700" /> Generate payment link</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Client email" className="h-8 bg-background border-border text-sm" data-testid={`pl-email-${req.id}`} />
          <Input list={`pl-services-${req.id}`} value={service} onChange={(e) => setService(e.target.value)} placeholder="Service name" className="h-8 bg-background border-border text-sm" data-testid={`pl-service-${req.id}`} />
          <datalist id={`pl-services-${req.id}`}>{services.map((s) => <option key={s} value={s} />)}</datalist>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="h-8 bg-background border-border text-sm" data-testid={`pl-amount-${req.id}`} />
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid={`pl-currency-${req.id}`}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </div>
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Notes / description for the client (optional)" className="h-8 bg-background border-border text-sm" data-testid={`pl-desc-${req.id}`} />
        <Button size="sm" disabled={creating} onClick={generate} className="rounded-sm bg-primary text-primary-foreground h-8 text-xs" data-testid={`pl-generate-${req.id}`}>
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CreditCard className="h-3.5 w-3.5 mr-1" /> Generate payment link</>}
        </Button>
      </div>

      {loading ? (
        <div className="py-4 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin text-orange-600" /></div>
      ) : links.length === 0 ? (
        <p className="text-xs text-muted-foreground">No payment links yet for this request.</p>
      ) : (
        <div className="space-y-2">
          {links.map((l) => {
            const s = STATUS[l.status] || STATUS.pending;
            return (
              <div key={l.session_id} className="rounded-md border border-border bg-card p-3" data-testid={`pl-link-${l.session_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.c}`} data-testid={`pl-status-${l.session_id}`}><s.Icon className="h-3 w-3" /> {s.t}</span>
                    <span className="text-sm font-medium text-foreground">{l.service_name}</span>
                    <span className="font-mono text-sm text-primary">{fmtAmt(l)}</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground">to {l.client_email}</span>
                </div>
                {l.description && <p className="mt-1 text-xs text-muted-foreground">{l.description}</p>}
                <div className="mt-2 flex items-center gap-1.5 rounded-sm border border-border bg-background px-2 py-1">
                  <span className="text-[11px] font-mono text-muted-foreground truncate flex-1" data-testid={`pl-url-${l.session_id}`}>{l.url}</span>
                  <button onClick={() => copy(l.url)} className="text-muted-foreground hover:text-orange-700 shrink-0" data-testid={`pl-copy-${l.session_id}`}><Copy className="h-3.5 w-3.5" /></button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={sending === l.session_id} onClick={() => sendEmail(l.session_id)} className="h-7 rounded-sm border-slate-300 bg-card text-xs" data-testid={`pl-send-email-${l.session_id}`}>
                    {sending === l.session_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5 mr-1" /> Email client</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => whatsapp(l)} className="h-7 rounded-sm border-green-300 text-green-700 bg-card hover:bg-green-50 text-xs" data-testid={`pl-whatsapp-${l.session_id}`}><MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
